import { useMemo } from 'react';
import { ActivityLogEntry } from '../types/business';
import { ACTIVITY_TYPE_LABELS, ACTIVITY_TYPES } from '../../shared/constants';

export interface ParsedActivity extends ActivityLogEntry {
  typeLabel: string;
  formattedDate: string;
  isAgentActivity: boolean;
  isClientActivity: boolean;
  isBusinessActivity: boolean;
  homeAddress?: string; // Add homeAddress field
}

export interface ActivityCategory {
  isAgent: boolean;
  isClient: boolean;
  isBusiness: boolean;
}

interface UseActivityParserReturn {
  parseActivities: (activities: ActivityLogEntry[]) => ParsedActivity[];
  getActivityTypeLabel: (type: number) => string;
  formatActivityDate: (dateString: string) => string;
  categorizeActivity: (type: number) => ActivityCategory;
}

// Exported pure functions for testing
export const getActivityTypeLabel = (type: number): string => {
  return ACTIVITY_TYPE_LABELS[type] || 'Unknown Activity';
};

export const formatActivityDate = (dateString: string): string => {
  try {
    // Handle SQLite datetime format "YYYY-MM-DD HH:MM:SS"
    // Add 'Z' to indicate UTC if the string doesn't already have timezone info
    const normalizedDateString = dateString.includes('T')
      ? dateString
      : dateString.replace(' ', 'T') + 'Z';

    const date = new Date(normalizedDateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours === 1 ? '' : 's'} ago`;
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      const diffInDays = Math.floor(diffInHours / 24);
      if (diffInDays < 7) {
        return `${diffInDays} day${diffInDays === 1 ? '' : 's'} ago`;
      } else {
        return date.toLocaleDateString();
      }
    }
  } catch (error) {
    console.warn('Error formatting activity date:', dateString, error);
    return 'Unknown date';
  }
};

export const categorizeActivity = (type: number): ActivityCategory => {
  const isAgent = type >= 10 && type <= 19;
  const isClient = (type >= 1 && type <= 9) || (type >= 20 && type <= 49);
  const isBusiness = type >= 50 && type <= 99;

  return {
    isAgent,
    isClient,
    isBusiness
  };
};

export const parseActivities = (activities: ActivityLogEntry[]): ParsedActivity[] => {
  return activities.map(activity => {
    const typeLabel = getActivityTypeLabel(activity.type);
    const formattedDate = formatActivityDate(activity.createdAt);
    const { isAgent, isClient, isBusiness } = categorizeActivity(activity.type);

    // Extract homeAddress from metadata for HOME_ADDED activities
    let homeAddress: string | undefined;
    if (activity.type === ACTIVITY_TYPES.HOME_ADDED && activity.metadata) {
      try {
        const metadata = typeof activity.metadata === 'string'
          ? JSON.parse(activity.metadata)
          : activity.metadata;
        homeAddress = metadata.homeAddress || undefined;
      } catch {
        console.warn('Failed to parse activity metadata for homeAddress:', activity.metadata);
      }
    }

    return {
      ...activity,
      typeLabel,
      formattedDate,
      isAgentActivity: isAgent,
      isClientActivity: isClient,
      isBusinessActivity: isBusiness,
      homeAddress
    };
  });
};

export const useActivityParser = (): UseActivityParserReturn => {
  const memoizedGetActivityTypeLabel = useMemo(() => getActivityTypeLabel, []);
  const memoizedFormatActivityDate = useMemo(() => formatActivityDate, []);
  const memoizedCategorizeActivity = useMemo(() => categorizeActivity, []);
  const memoizedParseActivities = useMemo(() => parseActivities, []);

  return {
    parseActivities: memoizedParseActivities,
    getActivityTypeLabel: memoizedGetActivityTypeLabel,
    formatActivityDate: memoizedFormatActivityDate,
    categorizeActivity: memoizedCategorizeActivity
  };
};